/**
 * Firestore layer for Supplier Evaluation workflow.
 * Database: sensei-workflows  (getWorkflowsDb)
 * Collection: supplier-evaluation/{id}
 * Subcollections:
 *   metadata/assessment_form
 *   responses/supplier_answers
 *   scoring/auditor_scores  (legacy — merged into responses in newer flow)
 *   analysis/ai_report
 *
 * Unauthenticated writes allowed on responses/supplier_answers (supplier portal).
 * All other writes require auth.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { getWorkflowsDb } from "@/platform/firebase/client";
import type {
  SupplierEvaluationProject,
  AssessmentForm,
  SEWorkflowState,
  SupplierCollaborator,
  EvaluatorUser,
} from "@/modes/agentic-workflows/supplier-evaluation/types";

// ─── Path helpers ─────────────────────────────────────────────────────────────

const COL = "supplier-evaluation";
function db() { return getWorkflowsDb(); }
function projectRef(id: string) { return doc(db(), COL, id); }
function subDoc(id: string, sub: string, docId: string) {
  return doc(db(), COL, id, sub, docId);
}

// ─── Project CRUD ─────────────────────────────────────────────────────────────

export async function createEvaluation(
  userId: string,
  userEmail: string,
  data: {
    project_name: string;
    supplier_info: SupplierEvaluationProject["supplier_info"];
    evaluation_focus: string[];
    additional_context?: string;
  },
): Promise<string> {
  const id = `se-${Date.now()}`;
  const { additional_context, ...rest } = data;
  await setDoc(projectRef(id), {
    evaluation_id: id,
    ...rest,
    ...(additional_context ? { additional_context } : {}),
    workflow_state: "created",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by_uid: userId,
    created_by_email: userEmail,
  });
  return id;
}

export async function getEvaluation(id: string): Promise<SupplierEvaluationProject | null> {
  const snap = await getDoc(projectRef(id));
  return snap.exists() ? (snap.data() as SupplierEvaluationProject) : null;
}

export async function getUserEvaluations(
  userId: string,
  userEmail?: string,
): Promise<SupplierEvaluationProject[]> {
  const queries = [
    getDocs(query(collection(db(), COL), where("created_by_uid", "==", userId), limit(50))),
  ];
  if (userEmail) {
    queries.push(
      getDocs(query(collection(db(), COL), where("evaluator_uids", "array-contains", userEmail), limit(50))),
    );
  }
  const snaps = await Promise.all(queries);
  const map = new Map<string, SupplierEvaluationProject>();
  snaps.flatMap((s) => s.docs).forEach((d) => map.set(d.id, d.data() as SupplierEvaluationProject));
  return Array.from(map.values()).sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export async function updateEvaluation(
  id: string,
  data: Partial<SupplierEvaluationProject>,
): Promise<void> {
  await updateDoc(projectRef(id), { ...data, updated_at: new Date().toISOString() });
}

export async function deleteEvaluation(id: string): Promise<void> {
  await deleteDoc(projectRef(id));
}

// ─── State transitions ────────────────────────────────────────────────────────

export async function transitionState(
  id: string,
  newState: SEWorkflowState,
  extraFields?: Record<string, unknown>,
): Promise<void> {
  await updateDoc(projectRef(id), {
    workflow_state: newState,
    updated_at: new Date().toISOString(),
    ...extraFields,
  });
}

// ─── Assessment form (metadata/assessment_form) ───────────────────────────────

export async function saveAssessmentForm(id: string, form: AssessmentForm): Promise<void> {
  await setDoc(subDoc(id, "metadata", "assessment_form"), {
    ...form,
    saved_at: new Date().toISOString(),
  });
}

export async function getAssessmentForm(id: string): Promise<AssessmentForm | null> {
  const snap = await getDoc(subDoc(id, "metadata", "assessment_form"));
  return snap.exists() ? (snap.data() as AssessmentForm) : null;
}

// ─── Supplier responses (responses/supplier_answers) ─────────────────────────
// Unauthenticated writes are allowed by Firestore rules on this subcollection.

export async function saveSupplierDraftResponses(id: string, responses: unknown): Promise<void> {
  await setDoc(
    subDoc(id, "responses", "supplier_answers"),
    { ...(responses as Record<string, unknown>), updated_at: new Date().toISOString() },
    { merge: true },
  );
}

export async function saveSupplierResponses(id: string, responses: unknown): Promise<void> {
  await setDoc(subDoc(id, "responses", "supplier_answers"), {
    ...(responses as Record<string, unknown>),
    submitted_at: new Date().toISOString(),
  });
  await transitionState(id, "responded", {
    supplier_completed_at: new Date().toISOString(),
  });
}

export async function getSupplierResponses(id: string): Promise<Record<string, unknown> | null> {
  const snap = await getDoc(subDoc(id, "responses", "supplier_answers"));
  return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
}

// ─── Analysis report (analysis/ai_report) ────────────────────────────────────

export async function getAnalysisReport(id: string): Promise<unknown | null> {
  const snap = await getDoc(subDoc(id, "analysis", "ai_report"));
  if (!snap.exists()) return null;
  const d = snap.data() as Record<string, unknown>;
  return d.report_json ? JSON.parse(d.report_json as string) : d;
}

export async function saveAnalysisReport(id: string, result: unknown): Promise<void> {
  await setDoc(subDoc(id, "analysis", "ai_report"), {
    report_json: JSON.stringify(result),
    saved_at: new Date().toISOString(),
  });
}

// ─── Supplier token ───────────────────────────────────────────────────────────

export async function validateSupplierToken(id: string, token: string): Promise<boolean> {
  const evaluation = await getEvaluation(id);
  if (!evaluation) return false;
  if (evaluation.supplier_token === token) return true;
  return evaluation.shared_with?.some((c) => c.token === token) ?? false;
}

// ─── Collaborators ────────────────────────────────────────────────────────────

export async function addCollaborator(id: string, collaborator: SupplierCollaborator): Promise<void> {
  await updateDoc(projectRef(id), {
    shared_with: arrayUnion(collaborator),
    updated_at: new Date().toISOString(),
  });
}

export async function removeCollaborator(id: string, collaborator: SupplierCollaborator): Promise<void> {
  await updateDoc(projectRef(id), {
    shared_with: arrayRemove(collaborator),
    updated_at: new Date().toISOString(),
  });
}

export async function touchCollaboratorActivity(id: string, token: string): Promise<void> {
  const evaluation = await getEvaluation(id);
  if (!evaluation?.shared_with) return;
  const updated = evaluation.shared_with.map((c) =>
    c.token === token ? { ...c, last_active: new Date().toISOString() } : c,
  );
  await updateDoc(projectRef(id), {
    shared_with: updated,
    updated_at: new Date().toISOString(),
  });
}

// ─── Evaluator team ───────────────────────────────────────────────────────────

export async function addEvaluatorUser(id: string, evaluator: EvaluatorUser): Promise<void> {
  await updateDoc(projectRef(id), {
    evaluator_users: arrayUnion(evaluator),
    evaluator_uids: arrayUnion(evaluator.email),
    updated_at: new Date().toISOString(),
  });
}

export async function removeEvaluatorUser(id: string, evaluator: EvaluatorUser): Promise<void> {
  await updateDoc(projectRef(id), {
    evaluator_users: arrayRemove(evaluator),
    evaluator_uids: arrayRemove(evaluator.email),
    updated_at: new Date().toISOString(),
  });
}
